import { useState } from "react"

import {
  useRequestProfileDeleteCodeMutation,
  useSubmitProfileDeleteCodeMutation,
  useVerifyProfileDeleteCodeMutation
} from "shared/api/user-space/mutations.generated"
import { Logger } from "shared/lib/Logger"
import { useBooleanState } from "shared/lib/useBooleanState"
import { useEvent } from "shared/lib/useEvent"

import { useAuth } from "./useAuth"

export function useDeleteProfile() {
  const { logout } = useAuth()
  const { value: isCodeCorrect, setValue: setIsCodeCorrect } =
    useBooleanState(false)
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(
    null
  )

  const [requestCode, { loading: isRequesting }] =
    useRequestProfileDeleteCodeMutation()
  const [verifyCode, { loading: isVerifying }] =
    useVerifyProfileDeleteCodeMutation()
  const [submitCode, { loading: isSubmitting }] =
    useSubmitProfileDeleteCodeMutation()

  const handleVerifyCode = useEvent((code: string) => {
    return verifyCode({ variables: { input: { code } } })
      .then(({ data }) => {
        if (data) {
          setRemainingAttempts(data?.verifyProfileDeleteCode.attemptsLeft)
          setIsCodeCorrect(data?.verifyProfileDeleteCode.valid)
          return !!data?.verifyProfileDeleteCode.valid
        }
        return false
      })
      .catch(() => {
        Logger.error(
          "CodeVerificationError",
          "unable to verify profile delete auth code"
        )
        return false
      })
  })

  const handleSubmitCode = useEvent((securityCode: string) => {
    return submitCode({
      variables: {
        input: {
          securityCode
        }
      }
    })
      .then(() => {
        logout()
        return true
      })
      .catch(() => {
        Logger.error("ProfileDeletionError", "unable to delete user profile")
        return false
      })
  })

  return {
    requestCode,
    verifyCode: handleVerifyCode,
    submitCode: handleSubmitCode,
    setIsCodeCorrect,
    setRemainingAttempts,
    remainingAttempts,
    isCodeCorrect,
    isRequesting,
    isVerifying,
    isSubmitting
  }
}
